import {Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CartService} from '../../services/cart.service';
import {LocaleFormat} from '../../interfaces/locale-format';
import {parseLocaleFormat} from '../../locales';

/**
 * Renders a summary of the contents of the cart.
 *
 * @order 3
 * @howToUse "With a different icon"
 * ```html
 * <cart-summary [icon]="'http://myapi/assets/icon.svg'"></cart-summary>
 * ```
 *
 * @howToUse "Display different words when the cart changes"
 * ```html
 * <cart-summary [noItemsText]="'Zero items'" [oneItemText]="'Single item'" [manyItemsText]="'Exactly # items'"></cart-summary>
 * ```
 *
 * @howToUse "Using always a number to display item count"
 * ```html
 * <cart-summary [noItemsText]="'# items'" [oneItemText]="'# items'" [manyItemsText]="'# items'"></cart-summary>
 * ```
 *
 * @note {info} Inputs that allows you to customize text also accept the special character `#` to use numbers instead of words to
 * specify quantity, for example `'# bla'` will update to `'0 bla'` or `'1 bla'` when the number of items in the cart change.
 */
@Component({
  selector: 'cart-summary',
  template: `<div class="cart-summary">
  <div class="cart-summary-icon">
    <svg *ngIf="!icon" version="1.1" class="summary-icon" xmlns="http://www.w3.org/2000/svg"
         xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 32 32">
      <path stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" stroke-linejoin="round"
            d="M2,4 L6,4 L10,20 L21,20 L25,10 L8,10 M13,10 L13,20 M18,20 L18,10 M10,15 L23,15"/>
      <circle fill="currentColor" cx="11" cy="24" r="2"/>
      <circle fill="currentColor" cx="21" cy="24" r="2"/>
    </svg>
    <img *ngIf="icon" [src]="icon" class="summary-icon" alt="cart-summary-icon">
  </div>
  <div class="cart-summary-contents">
    <div class="cart-summary-items">
      {{itemsText}}
    </div>
    <div class="cart-summary-cost">
      {{totalCost | currency:format.currencyCode:format.display:format.digitsInfo:format.locale}}
    </div>
  </div>
</div>
`,
})
export class CartSummaryComponent implements OnInit, OnChanges, OnDestroy {
  private _serviceSubscription: any;
  /**
   * The url of an icon to show on the summary. Use this to replace the default icon which is an svg with the image of a shopping cart.
   *
   * To use the default icon when you are using the `[icon]` input just set it to a falsy value, eg: `null`, `undefined`, `''`, etc.
   */
  @Input() icon: string;
  /**
   * The text to display when there are no items in the cart.
   */
  @Input() noItemsText = 'No items';
  /**
   * The text to display when there is only one item in the cart.
   */
  @Input() oneItemText = 'One item';
  /**
   * The text to display when there are several items in the cart.
   */
  @Input() manyItemsText = '# items';
  /**
   * Changes currency display format for the component. Overrides the value set from the service using `setCurrencyFormat`.
   */
  @Input() localeFormat: string;
  itemsText;
  totalItems = 0;
  totalCost = 0;
  format: LocaleFormat;

  constructor(private cartService: CartService<any>) {

  }

  private updateItemsText() {
    let text = this.noItemsText;
    if (this.totalItems > 0) {
      text = this.totalItems === 1 ? this.oneItemText : this.manyItemsText;
    }
    this.itemsText = text.replace('#', this.totalItems.toString());
  }

  private updateComponent() {
    this.totalItems = this.cartService.itemCount();
    this.totalCost = !this.cartService.isEmpty() ? this.cartService.totalCost() : 0;
    if (!this.localeFormat) {
      this.format = <LocaleFormat>this.cartService.getLocaleFormat(true);
    }
    this.updateItemsText();
  }

  ngOnInit(): void {
    this.updateComponent();
    this._serviceSubscription = this.cartService.onChange.subscribe(() => {
      this.updateComponent();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['localeFormat']) {
      this.format = this.localeFormat ?
        parseLocaleFormat(this.localeFormat) :
        <LocaleFormat>this.cartService.getLocaleFormat(true);
    }
    if (changes['noItemsText'] || changes['oneItemText'] || changes['manyItemsText']) {
      this.updateItemsText();
    }
  }

  ngOnDestroy(): void {
    this._serviceSubscription.unsubscribe();
  }
}
